import {
  asPlaybookId,
  collectPlaybookCriteria,
} from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconPencil } from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { appPaths } from "../../app/appPaths.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { useFilters } from "../filters/filters.js";
import { usePlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";
import { useSettingsSectionsPolicy } from "../settingsPage/settingsSectionsPolicy.js";
import { PlaybookCriteriaAggregatesTable } from "./criteria/playbookCriteriaAggregatesTable.js";
import { PlaybookCriterionAggregatesDashboard } from "./criteria/playbookCriterionAggregatesDashboard.js";

export const TrainingTeamPageByPlaybook: FC = () => {
  const navigate = useNavigate();
  const { playbookId: playbookIdParam, criterionId } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const canEditPlaybook =
    useSettingsSectionsPolicy().sections.playbooks.canAccess;

  const selectedCriterionId = criterionId ?? null;

  const [isTableFetching, setIsTableFetching] = useState(false);

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [, renderFilters] = useFilters(
    [
      "teammateGroupIds",
      "dateRange",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "dateBasis",
    ],
    { playbookId, view: "team" },
    playbookFilterDefaults,
  );

  // Get the full playbook
  const { data: currentPlaybook, isLoading } = usePlaybookByIdQuery(playbookId);

  // Memoize the criteria list to avoid recalculating on every render
  const allCriteriaNodes = useMemo(() => {
    return currentPlaybook
      ? collectPlaybookCriteria(currentPlaybook.assessment_tree)
      : [];
  }, [currentPlaybook]);

  // Memoize the finding of the selectedCriterion
  const selectedCriterion = useMemo(() => {
    return selectedCriterionId
      ? allCriteriaNodes.find(
          (criterion) => criterion.id === selectedCriterionId,
        )
      : undefined;
  }, [selectedCriterionId, allCriteriaNodes]);

  const renderPageContent = () => {
    if (isLoading) {
      return <DefaultSkeleton />;
    }

    if (!currentPlaybook) {
      return (
        <div>
          <div className="mb-4">
            Create your first playbook to view detailed performance analysis
            across your team.
          </div>
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => navigate(appPaths.settingsPlaybooks.build())}
          >
            Manage playbooks
          </Button>
        </div>
      );
    }

    return (
      <div
        className={twMerge(
          "grid h-full gap-4",
          selectedCriterion
            ? "grid-cols-[minmax(320px,500px)_1fr]"
            : "grid-cols-1",
        )}
      >
        {/* Left: Playbook list should scroll within its own column. Negative
            margin + padding lets the table's outer ring/shadow render without
            being clipped by the scroll container. */}
        <div className="relative -m-1 min-h-0 overflow-y-auto p-1">
          <PlaybookCriteriaAggregatesTable
            playbook={currentPlaybook}
            selectedCriterionId={selectedCriterionId ?? undefined}
            onFetchingChange={(isFetching) => setIsTableFetching(isFetching)}
          />
        </div>
        {selectedCriterion ? (
          // Right: scrollable container for cards + table
          <div className="relative -m-1 min-h-0 overflow-y-auto p-1">
            <PlaybookCriterionAggregatesDashboard
              playbook={currentPlaybook}
              criterion={selectedCriterion}
            />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="grid h-full grid-rows-[auto_minmax(0,1fr)] gap-4 overflow-hidden sm:gap-6">
      <PageHeader
        title="Playbook"
        actions={
          canEditPlaybook ? (
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              icon={<IconPencil className="size-4" />}
              onClick={() =>
                navigate(appPaths.settingsPlaybook.build({ playbookId }))
              }
            >
              Edit playbook
            </Button>
          ) : undefined
        }
        filterBar={renderFilters({
          // We'll just show a spinner here for the table. The dashboard has plenty loading states of its own.
          isLoading: isTableFetching,
          leadingItems: <PerformancePlaybookSwitcher />,
        })}
      />

      {/* The shared page gutter for all content states; each column inside
          manages its own scrolling. */}
      <div className="min-h-0 px-4 pb-4 sm:px-6 sm:pb-6">
        {renderPageContent()}
      </div>
      <NavigationSlideoverContainer />
    </div>
  );
};
